import { atom, selector, selectorFamily } from 'recoil';
import { ORDER_ASCENDING } from '../constants/app';
import { JOURNEY_SORT_START_DATE, JOURNEY_STATUS_ACTIVE, NO_COMMUNICATION_TIME_DEFAULT } from '../constants/journeys';
import { OrderDirection } from '../types/app';
import { Journey, JourneyCore, JourneySelectPoint, JourneySortOption } from '../types/journey';
import { roundUpToNextIntervalTime } from '../utils/dates';

export const loadingJourneysState = atom({
  key: 'app.client.journeys.loading',
  default: false,
});

export const journeysState = atom<Journey[] | undefined>({
  key: 'app.client.journeys',
  default: undefined,
});

export const journeyIdsState = selector<string[] | undefined>({
  key: 'app.client.journeyIds',
  get: ({ get }) => {
    const journeys = get(journeysState);
    if (!journeys) return;
    return journeys.map((journey) => journey._id);
  },
});

export const journeyState = selectorFamily<Journey | undefined, string>({
  key: 'app.client.journey',
  get:
    (id) =>
    ({ get }) => {
      const journeys = get(journeysState);
      if (!journeys) return;
      return journeys.find((journey) => journey._id === id);
    },
});

export const selectedJourneyIdState = atom<string | undefined>({
  key: 'app.client.journeys.selectedId',
  default: undefined,
});

export const selectedJourneyState = selector({
  key: 'app.client.journeys.selected',
  get: ({ get }) => {
    const selectedJourneyId = get(selectedJourneyIdState);
    if (!selectedJourneyId) return;
    return get(journeyState(selectedJourneyId));
  },
});

export const editingJourneyState = atom<JourneyCore | undefined>({
  key: 'app.client.journeys.editing',
  default: selector({
    key: 'app.client.journeys.editing.initializer',
    get: ({ get }) => {
      const selectedJourneyId = get(selectedJourneyIdState);
      if (selectedJourneyId === undefined) return;
      if (selectedJourneyId === '') {
        const initialDate = roundUpToNextIntervalTime();
        return {
          _id: '',
          asset: undefined,
          destinationAddress: undefined,
          endDate: initialDate,
          endPoint: undefined,
          maxTimeNoCommunication: NO_COMMUNICATION_TIME_DEFAULT,
          originAddress: undefined,
          startDate: initialDate,
          startPoint: undefined,
        };
      }
      return get(journeyState(selectedJourneyId));
    },
  }),
});

export const updateJourneysState = atom<string | undefined>({
  key: 'app.client.journeys.update',
  default: undefined,
});

export const searchJourneyState = atom<string | undefined>({
  key: 'app.client.journeys.search',
  default: undefined,
});

export const orderDirectionState = atom<OrderDirection>({
  key: 'app.client.journeys.orderDirection',
  default: ORDER_ASCENDING,
});

export const sortJourneysState = atom<JourneySortOption>({
  key: 'app.client.journeys.sort',
  default: JOURNEY_SORT_START_DATE,
});

export const filterStatusState = atom<number | undefined>({
  key: 'app.client.journeys.filter.status',
  default: JOURNEY_STATUS_ACTIVE,
});

export const journeySelectPointState = atom<JourneySelectPoint | undefined>({
  key: 'app.client.journey.selectPoint',
  default: undefined,
});
